import React, { useState } from 'react';
import { DateTime } from 'luxon';

/**
 * What the spreadsheet could never answer.
 *
 * Each panel here exists because a specific question had no answer before:
 * where are we turning work away, what price do we lose at, which kind of
 * prospect is worth the follow-up, and is the top of the funnel growing or
 * quietly drying up.
 */

const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : '—');

function Bars({ rows, labelOf, valueOf, noteOf, tone }) {
    const max = Math.max(1, ...rows.map(valueOf));
    if (!rows.length) return <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Nothing recorded yet.</p>;
    return rows.map((r, i) => (
        <div className="lds__barRow" key={i}>
            <span className="lds__barLabel" title={labelOf(r)}>{labelOf(r)}</span>
            <span className="lds__barTrack">
                <span
                    className={`lds__barFill${tone ? ` lds__barFill--${tone}` : ''}`}
                    style={{ width: `${(valueOf(r) / max) * 100}%` }}
                />
            </span>
            <span className="lds__barValue">{noteOf ? noteOf(r) : valueOf(r)}</span>
        </div>
    ));
}

// A year at a time. Twenty-six months squeezed into one row made every recent
// column a sliver; the arrows page through instead of shrinking everything.
const WINDOW = 12;
const monthLabel = (ym) => DateTime.fromISO(`${ym}-01`).toFormat('yy LLL');

export default function LeadsInsights({ stats, labels }) {
    const [yearsBack, setYearsBack] = useState(0);
    if (!stats) return null;
    const t = stats.totals || {};
    const label = (group, key) => labels?.[group]?.[key] || key;

    const months = stats.by_month || [];
    const end = Math.max(0, months.length - yearsBack * WINDOW);
    const start = Math.max(0, end - WINDOW);
    const shown = months.slice(start, end);
    const canGoBack = start > 0;
    const canGoForward = yearsBack > 0;
    // Scaled to the window on screen, not to all time: a 108-inquiry month two
    // years ago should not flatten the year you are actually looking at.
    const maxMonth = Math.max(1, ...shown.map((m) => m.inquiries));

    return (
        <div className="lds__panels">
            <div className="lds__panel" style={{ gridColumn: '1 / -1' }}>
                <div className="lds__panelHead">
                    <div>
                        <h4 className="lds__panelTitle">Inquiries by month</h4>
                        <p className="lds__panelNote">
                            Green is a booking taken; amber is an account opened that has not
                            booked yet. The height is the top of the funnel — the number that
                            decides everything downstream.
                        </p>
                    </div>
                    <div className="lds__pager">
                        <span className="lds__pagerRange">
                            {shown.length
                                ? `${monthLabel(shown[0].month)} – ${monthLabel(shown[shown.length - 1].month)}`
                                : 'no data'}
                        </span>
                        <button className="lds__pagerBtn" onClick={() => setYearsBack((y) => y + 1)}
                            disabled={!canGoBack} aria-label="Previous year" title="A year earlier">‹</button>
                        <button className="lds__pagerBtn" onClick={() => setYearsBack((y) => Math.max(0, y - 1))}
                            disabled={!canGoForward} aria-label="Next year" title="A year later">›</button>
                    </div>
                </div>
                <div className="lds__months">
                    {shown.map((m) => {
                        const pctOf = (n) => (m.inquiries ? (n / m.inquiries) * 100 : 0);
                        const booked = m.booked ?? 0;
                        const registered = m.registered ?? 0;
                        return (
                            <div className="lds__month" key={m.month}>
                                <span className="lds__monthTop">{m.inquiries}</span>
                                <span className="lds__monthBar" style={{ height: `${(m.inquiries / maxMonth) * 100}%` }}>
                                    {/* Stacked from the floor: bookings, then the accounts
                                        that opened and have not booked. Both were one green
                                        band before, which flattered the months where nobody
                                        actually booked anything. */}
                                    <span className="lds__monthReg" style={{ height: `${pctOf(registered)}%` }} />
                                    <span className="lds__monthWon" style={{ height: `${pctOf(booked)}%` }} />
                                    {/* Anchored to the BAR, not the column: hung off the
                                        column it floated at the top of the chart, nowhere
                                        near the thing being pointed at. */}
                                    <span className="lds__tip">
                                        <strong>{monthLabel(m.month)}</strong>
                                        <span>{m.inquiries} {m.inquiries === 1 ? 'inquiry' : 'inquiries'}</span>
                                        <span>{booked} booked · {pctOf(booked).toFixed(1)}%</span>
                                        {registered > 0 && (
                                            <span>{registered} registered, no booking yet</span>
                                        )}
                                    </span>
                                </span>
                                <span className="lds__monthLabel">{monthLabel(m.month)}</span>
                            </div>
                        );
                    })}
                    {!shown.length && (
                        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No inquiries in this year.</p>
                    )}
                </div>
            </div>

            <div className="lds__panel">
                <h4 className="lds__panelTitle">Why we lose them</h4>
                <p className="lds__panelNote">Only leads marked not interested.</p>
                <Bars
                    rows={stats.lost_reasons || []}
                    labelOf={(r) => (r.reason === 'unrecorded' ? 'No reason recorded' : label('lost_reason', r.reason))}
                    valueOf={(r) => r.n}
                    tone="lost"
                />
            </div>

            <div className="lds__panel">
                <h4 className="lds__panelTitle">Areas we turn away</h4>
                <p className="lds__panelNote">
                    Every lead lost for being outside the coverage, by area. This is the
                    expansion argument, counted rather than remembered.
                </p>
                <Bars
                    rows={stats.uncovered_areas || []}
                    labelOf={(r) => r.area}
                    valueOf={(r) => r.n}
                    tone="lost"
                />
            </div>

            <div className="lds__panel">
                <h4 className="lds__panelTitle">Who signs up</h4>
                <p className="lds__panelNote">
                    Registered or booked, out of everyone of that kind who got in touch.
                </p>
                <Bars
                    rows={stats.by_type || []}
                    labelOf={(r) => label('lead_type', r.lead_type)}
                    valueOf={(r) => r.n}
                    noteOf={(r) => `${r.converted}/${r.n}`}
                    tone="won"
                />
            </div>

            <div className="lds__panel">
                <h4 className="lds__panelTitle">Where they come from</h4>
                <p className="lds__panelNote">
                    Every imported lead is marked WhatsApp — the sheet never asked, and that is
                    where they arrived. From here on this is the panel that says where to spend.
                </p>
                <Bars
                    rows={stats.by_source || []}
                    labelOf={(r) => (r.source === 'unrecorded' ? 'Not recorded' : r.source)}
                    valueOf={(r) => r.n}
                    noteOf={(r) => `${r.converted}/${r.n}`}
                />
            </div>

            <div className="lds__panel">
                <h4 className="lds__panelTitle">The funnel</h4>
                <p className="lds__panelNote">Of everyone who got in touch.</p>
                <Bars
                    rows={[
                        { k: 'Got in touch', n: t.inquiries || 0 },
                        { k: 'Still in play or won', n: stats.funnel?.reached || 0 },
                        { k: 'Quoted', n: stats.funnel?.quoted || 0 },
                        { k: 'Registered', n: stats.funnel?.registered || 0 },
                        { k: 'Made a booking', n: stats.funnel?.booked || 0 },
                    ]}
                    labelOf={(r) => r.k}
                    valueOf={(r) => r.n}
                    noteOf={(r) => `${r.n} · ${pct(r.n, t.inquiries || 0)}`}
                />
                <div style={{ marginTop: 16, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                    <div>
                        Average time to convert:{' '}
                        <strong>{t.avg_days_to_convert == null ? '—' : `${Math.round(t.avg_days_to_convert)} days`}</strong>
                    </div>
                </div>
            </div>
        </div>
    );
}
